import {
  Alert,
  Box,
  Button,
  Chip,
  DialogActions,
  DialogContent,
  LinearProgress,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { DialogHeader } from '@/components/common/DialogHeader';
import { StockLocationBalances } from './StockLocationBalances';
import type { InventoryItem } from '@/types';
import type { StockLevelItem } from '@/hooks/useInventory';

export function StockLocationsDialog({
  item,
  levels,
  open,
  onClose,
  loading,
  error,
  onRetry,
}: {
  item: InventoryItem | null;
  levels: StockLevelItem[];
  open: boolean;
  onClose: () => void;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
}) {
  const { t } = useTranslation('inventory');
  const { t: tc } = useTranslation('common');
  if (!item) return null;
  return (
    <ResponsiveDialog
      open={open}
      onClose={onClose}
      mobileTitle={t('stockLocations.title')}
      maxWidth="md"
      fullWidth
    >
      <DialogHeader title={t('stockLocations.title')} onClose={onClose} />
      <DialogContent dividers>
        <Box sx={{ mb: 2 }}>
          <Typography fontWeight={600}>{item.name}</Typography>
          <Typography variant="body2">{item.partNumber}</Typography>
          {!!item.tags?.length && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2">{t('partForm.keywords')}</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {item.tags.map((tag) => <Chip key={tag} label={tag} size="small" />)}
              </Box>
            </Box>
          )}
        </Box>
        {loading ? (
          <LinearProgress aria-label={tc('loading')} />
        ) : error ? (
          <Alert
            severity="error"
            action={<Button onClick={onRetry}>{tc('errors.refresh')}</Button>}
          >
            {t('errorLoading')}
          </Alert>
        ) : (
          <StockLocationBalances levels={levels} />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{tc('close')}</Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}
